import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import '@polymer/paper-input/paper-textarea';
import '@polymer/neon-animation/neon-animation.js';
import '../katapult-auth/katapult-auth.js';
import '../paper-table/paper-table';
import '../katapult-elements/katapult-icon.js';
import '../katapult-elements/katapult-button.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';
import '@shoelace-style/shoelace/dist/components/breadcrumb/breadcrumb.js';
import '@shoelace-style/shoelace/dist/components/breadcrumb-item/breadcrumb-item.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';
import { PaperTableStyle } from '../../elements/style-modules/paper-table-style.js';
import { PaperDialogStyle } from '../../elements/style-modules/paper-dialog-style';
import { KatapultScrollbars } from '../../elements/style-modules/katapult-scrollbars';
import { FlexStyle } from '../../elements/style-modules/flex';
import { getActiveWord, getSelectionCoords, replaceActiveWord } from '../../modules/CursorHelpers.js';
import { CamelCase } from '../../modules/CamelCase.js';
import { ToArray } from '../../modules/ToArray.js';
import { FormatDate } from '../../modules/Date.js';
import { GeofireTools } from '../../modules/GeofireTools.js';
import { isFlagEnabled } from '../../modules/FeatureFlags.js';
import { LitElement, html, css, nothing, unsafeCSS } from 'lit';
import { map } from 'lit/directives/map.js';
import { when } from 'lit/directives/when.js';
import { until } from 'lit/directives/until.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { classMap } from 'lit/directives/class-map.js';
import { moveConversationToUserInbox, removeConversationFromUserInbox } from './Feedback.js';
import { getFeedbackEntityLabels } from '../feedback/Feedback.js';
import { ShouldUpdate } from '../../modules/ShouldUpdate.js';
import { CompressPhoto } from '../../modules/CompressPhoto.js';
import { logger } from '@sentry/browser';
/**
 * @typedef {'issue_open'|'issue_close'|'conversation_join'|'conversation_leave'} ConversationEventTypes
 */

export class FeedbackChat extends LitElement {
  static properties = {
    entityLabels: { type: Object },
    subentity: { type: Object },
    conversation: { type: Object },
    conversationRef: { type: Object },
    messages: { type: Array },
    userIsSubscribed: { type: Boolean },
    commentsLoaded: { type: Boolean },
    userHasDraft: { type: Boolean },
    showMentionList: { type: Boolean },
    _focused: { type: Boolean, shouldUpdate: false },
    filteredUsernames: { type: Array },
    allCompanyUsers: { type: Array },
    allCompanyUsernames: { type: Array },
    userGroup: { type: String },
    userId: { type: String },
    nodes: { type: Object },
    mapStyles: { type: Object },
    conversationRefPath: { type: String },
    showBackButton: { type: Boolean },
    _messageAttachments: { type: Array },
    _isPublicChat: { type: Boolean }
  };

  static styles = [
    unsafeCSS(PaperTableStyle),
    unsafeCSS(PaperDialogStyle),
    unsafeCSS(KatapultScrollbars),
    unsafeCSS(FlexStyle),
    css`
      :host {
        position: relative;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        gap: var(--sl-spacing-small);
      }
      [hidden] {
        display: none !important;
      }
      #title {
        font-size: 14pt;
      }
      paper-spinner {
        --paper-spinner-color: var(--secondary-color);
      }
      paper-row {
        height: auto;
      }
      paper-cell[textarea] {
        padding: 0;
      }
      paper-checkbox {
        --primary-color: var(--secondary-color);
      }
      paper-row[completed] {
        opacity: 0.7;
      }
      paper-row[completed] paper-textarea {
        --iron-autogrow-textarea: {
          text-decoration: line-through;
        };
      }
      [slot='sortable'] paper-row {
        transition: background-color 0.3s;
      }
      paper-row:hover {
        z-index: 2;
      }
      [slot='sortable'] paper-row:not([slot='after']):hover,
      .dragging {
        background: var(--paper-grey-50);
      }
      [hover] {
        opacity: 0;
        transition: opacity 0.3s;
      }
      [hide-hover] {
        opacity: 1;
        transition: opacity 0.3s;
      }
      [slot='sortable'] paper-row:not([slot='after']):hover [hover] {
        opacity: 1;
      }
      [slot='sortable'] paper-row:hover [hide-hover] {
        opacity: 0;
      }
      paper-item:hover {
        cursor: pointer;
      }
      #commentsContainer {
        max-height: 30vh;
        overflow-y: auto;
        overflow-x: hidden;
        padding: 16px 8px;
        display: flex;
        flex-direction: column;
      }
      .comment {
        margin-top: 16px;
        width: 100%;
      }
      .comment > .commentBody {
        border: 1px solid rgba(0, 0, 0, 0.06);
        padding: 8px 12px;
        white-space: pre-line;
        border-radius: 20px;
      }
      .commentSent > .commentBody {
        border-bottom-right-radius: 0;
        background-color: rgba(0, 114, 153, 0.12);
        margin-left: 40px;
        justify-self: flex-end;
      }
      .commentReceived > .commentBody {
        display: block !important;
        border-bottom-left-radius: 0;
        background-color: rgba(0, 0, 0, 0.06);
        margin-right: 40px;
        justify-self: flex-start;
      }
      .event > .commentBody {
        display: flex;
        flex-direction: row-reverse;
        background-color: white;
        border: none;
        font-style: italic;
        color: grey;
      }
      .senderDetails {
        display: flex;
        align-items: center;
        margin-top: var(--sl-spacing-2x-small);
      }
      .commentSent .senderDetails {
        flex-direction: row-reverse;
      }
      .event > .senderDetails {
        color: grey;
      }
      .messageChip {
        transform: scale(0.5);
        height: 20px;
        width: 20px;
      }
      .chipContainer {
        height: 20px;
        width: 20px;
      }
      #messageInputContainer {
        position: relative;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        flex-grow: 1;
        background-color: rgba(0 0 0 / 6%);
        border-radius: 20px;
      }
      #messageInput {
        flex: 1;
        flex-basis: auto;
        white-space: pre-wrap;
        padding: 12px;
        outline: none;
      }
      #messageInputLabel {
        position: absolute;
        user-select: none;
        opacity: 0.7;
        pointer-events: none;
        padding: 12px;
        top: 0;
      }
      #messageAttachmentsContainer {
        display: flex;
        width: 100%;
        padding: 0 var(--sl-spacing-small);
        gap: var(--sl-spacing-small);
        flex-wrap: wrap;
      }
      #messageButtonsContainer {
        display: flex;
        gap: var(--sl-spacing-3x-small);
        margin-left: auto;
        justify-content: flex-end;
      }
      .mention {
        font-weight: bold;
        color: var(--secondary-color);
      }
      #mentionList {
        position: absolute;
        width: 200px;
        max-height: 300px;
        transform-origin: bottom left;
        z-index: 2;
      }
      #mentionList:not(:hover) paper-row:first-of-type {
        background-color: rgba(0 0 0 / 6%);
      }
      #mentionList paper-row:hover {
        background-color: rgba(0 0 0 / 6%);
      }
      .actionIconButton {
        cursor: pointer;
        margin: var(--sl-spacing-x-small);
        color: var(--primary-color);
      }
      sl-breadcrumb::part(base) {
        row-gap: var(--sl-spacing-x-small);
      }
      sl-breadcrumb-item katapult-icon {
        color: var(--primary-color);
      }
      sl-breadcrumb-item::part(label) {
        color: var(--sl-color-gray-600);
        cursor: default;
      }
      .tagContainer {
        border-radius: var(--sl-border-radius-pill);
        background-color: var(--sl-color-gray-100);
        display: flex;
        padding: var(--sl-spacing-2x-small);
        align-items: center;
        display: flex;
      }
      #actionSubentityLabel {
        font-style: italic;
        font-size: 14px;
        margin-left: 10px;
        text-wrap: wrap;
      }
      .issue-button-divider {
        border-left: 1px solid var(--katapult-button-border-color);
        position: absolute;
        top: -8px;
        height: 40px;
        left: 32px;
      }
      .messageThumbnail {
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        height: 128px;
        width: 128px;
        border-radius: var(--sl-border-radius-large);
        overflow: hidden;
        background: var(--sl-color-neutral-300);
      }
      .messageThumbnail katapult-icon {
        position: absolute;
        top: var(--sl-spacing-x-small);
        right: var(--sl-spacing-x-small);
        color: var(--sl-color-neutral-900);
        background: var(--sl-color-neutral-0);
        opacity: 0.8;
        border-radius: var(--sl-border-radius-circle);
      }
      .messageThumbnail katapult-icon:hover {
        cursor: pointer;
      }
      .messageThumbnail > img {
        height: 100%;
        width: 100%;
        object-fit: cover;
      }

      .commentSent .attachment {
        justify-self: flex-end;
      }

      .attachment {
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        overflow: hidden;
        border-radius: var(--sl-border-radius-large);
      }

      div.attachment.loading {
        height: 128px;
        width: 128px;
        background: var(--sl-color-neutral-300);
      }

      div.attachment.chip {
        padding: var(--sl-spacing-x-small);
        gap: var(--sl-spacing-x-small);
        background: var(--sl-color-neutral-300);
      }

      img.attachment {
        height: 256px;
        max-height: fit-content;
        max-width: -webkit-fill-available;
      }
    `
  ];

  async _renderAsyncImageTemplate(urlPromise) {
    const url = await urlPromise;
    return html`<img src=${url} />`;
  }

  _renderAttachmentLoadingTemplate() {
    return html`
      <div class="attachment loading">
        <sl-spinner style="font-size: 1.5rem;"></sl-spinner>
      </div>
    `;
  }

  async _renderAsyncAttachmentTemplate(attachmentId) {
    const attachmentLookup = (this._attachmentLookup[attachmentId] ??= {});
    const attachmentRef = firebase.storage().ref(`feedback/${this.userGroup}/${attachmentId}`);
    const metadata = await (attachmentLookup.metadata ??= attachmentRef.getMetadata());
    if (metadata.contentType.startsWith('image/')) {
      // Get the full image url, caching it in the lookup object
      const fullImageUrl = await (attachmentLookup.url ??= attachmentRef.getDownloadURL());
      const thumbnailUrl = await (attachmentLookup.thumbnailUrl ??= this.getPhotoThumbnailUrl(attachmentId, fullImageUrl));
      // Return the image element with a click handler to open the full image
      return html`<img
        class="attachment"
        style="cursor: pointer;"
        src=${thumbnailUrl}
        @click=${() => window.open(fullImageUrl, '_blank')}
      />`;
    }
    return html`
      <div class="attachment chip">
        <katapult-icon icon="attachment"></katapult-icon>
        <span>${metadata.contentType}</span>
      </div>
    `;
  }

  async getPhotoThumbnailUrl(attachmentId, fallbackImageUrl) {
    try {
      const thumbnailRef = firebase.storage().ref(`feedback/${this.userGroup}/${attachmentId}_thumbnail`);
      const thumbnailUrl = await thumbnailRef.getDownloadURL();
      return thumbnailUrl;
    } catch (e) {
      return fallbackImageUrl;
    }
  }

  _renderAttachmentTemplate(attachmentId) {
    const attachmentTemplate = this._renderAsyncAttachmentTemplate(attachmentId);
    return until(attachmentTemplate, this._renderAttachmentLoadingTemplate());
  }

  _renderMessageTemplate(message) {
    const { text: messageText, sent_by: senderUid, sent_at: timestamp, attachment_id: attachmentId } = message;
    const commentClassMap = this.getCommentClassMap(message) ?? {};
    const styleClasses = { comment: true, ...commentClassMap };
    const isAttachment = attachmentId != null;
    return html`
      <div class=${classMap(styleClasses)}>
        ${when(
          isAttachment,
          this._renderAttachmentTemplate.bind(this, attachmentId),
          () => html` <div class="commentBody">${messageText}</div> `
        )}
        <div class="senderDetails">
          <div class="chipContainer">
            <user-chip class="messageChip" .uid=${senderUid} hide-name></user-chip>
          </div>
          <span style="margin: 0 10px;">${this.formatDateSeconds(timestamp?.seconds)}</span>
        </div>
      </div>
    `;
  }

  render() {
    const messageImageAttachments = this._messageAttachments.filter(({ file }) => file?.type.startsWith('image/'));
    const maySendMessage = this.userHasDraft || messageImageAttachments.length > 0;
    const { showBackButton } = this;
    const inUserInbox = this.conversation.queued_for?.includes(this.userId);
    const hasUnresolvedIssue = this.conversation.issue_status === 'UNRESOLVED';
    const hasMessages = this.messages.length > 0;
    return html`
      <div style="display: flex; flex-direction: column; gap: var(--sl-spacing-small);">
        ${when(
          hasMessages || showBackButton,
          () => html`
            <div flex align-center justify-end style="gap: 8px;">
              ${when(
                showBackButton,
                () => html`
                  <katapult-button
                    style="margin-right: auto;"
                    icon="arrow_back"
                    size="small"
                    @click=${() => this.dispatchEvent(new CustomEvent('close'))}
                    >Go Back</katapult-button
                  >
                `
              )}
              ${when(
                hasMessages,
                () => html`
                  ${when(
                    this.userIsSubscribed,
                    () => html`
                      <katapult-button
                        size="small"
                        icon=${inUserInbox ? 'archive' : 'unarchive'}
                        @click=${inUserInbox ? this.removeFromInbox : this.sendToInbox}
                        >${inUserInbox ? 'Remove from Inbox' : 'Move to Inbox'}</katapult-button
                      >
                    `
                  )}
                  <katapult-button
                    size="small"
                    icon=${this.userIsSubscribed ? 'output' : 'input'}
                    @click=${this.toggleJoinLeaveConversation}
                    >${this.userIsSubscribed ? 'Leave Conversation' : 'Join Conversation'}</katapult-button
                  >
                `
              )}
            </div>
          `
        )}
        <sl-breadcrumb>
          <!-- Job Tag -->
          ${
            this.entityLabels.job
              ? html`
                  <sl-breadcrumb-item>
                    <div class="tagContainer" pointer @click=${() => this.openBreadcrumb(this.conversation, 'job')}>
                      <sl-tooltip placement="bottom" content="Open Job" hoist>
                        <div style="display: flex; align-items: center;">
                          <katapult-icon icon="place" size="20"></katapult-icon>
                          <span style="margin: 0 var(--sl-spacing-2x-small)">${this.entityLabels.job}</span>
                        </div>
                      </sl-tooltip>
                    </div>
                  </sl-breadcrumb-item>
                `
              : nothing
          }
          <!-- Node Tag -->
          ${
            this.entityLabels.node
              ? html`
                  <sl-breadcrumb-item>
                    <div class="tagContainer" pointer @click=${() => this.openBreadcrumb(this.conversation, 'node')}>
                      <sl-tooltip placement="bottom" content="Open Node" hoist>
                        <div style="display: flex; align-items: center;">
                          <katapult-icon icon="radio_button_unchecked" size="20"></katapult-icon>
                          <span style="margin: 0 var(--sl-spacing-2x-small)">${this.entityLabels.node}</span>
                        </div>
                      </sl-tooltip>
                    </div>
                  </sl-breadcrumb-item>
                `
              : nothing
          }

          <!-- TODO (02-27-2023): We don't have a good way to link to these yet -->
          <!-- Action Tag -->
          ${
            this.entityLabels.action
              ? html`
                  <sl-breadcrumb-item>
                    <div class="tagContainer">
                      <sl-tooltip placement="bottom" content="Action" hoist>
                        <div style="display: flex; align-items: center;">
                          <katapult-icon icon="show_chart" size="20"></katapult-icon>
                          <span style="margin: 0 var(--sl-spacing-2x-small)">${this.entityLabels.action}</span>
                        </div>
                      </sl-tooltip>
                    </div>
                  </sl-breadcrumb-item>
                `
              : nothing
          }

          <!-- Event Attribute Tag -->
          ${
            this.entityLabels.event_attribute
              ? html`
                  <sl-breadcrumb-item>
                    <div class="tagContainer" ]>
                      <sl-tooltip placement="bottom" content="Event Attribute" hoist>
                        <div style="display: flex; align-items: center;">
                          <katapult-icon icon="calendar_today" size="20"></katapult-icon>
                          <span style="margin: 0 var(--sl-spacing-2x-small)">${this.entityLabels.event_attribute}</span>
                        </div>
                      </sl-tooltip>
                    </div>
                  </sl-breadcrumb-item>
                `
              : nothing
          }

          <!-- Event Instance Tag -->
          ${
            this.entityLabels.event_instance
              ? html`
                  <sl-breadcrumb-item>
                    <div class="tagContainer">
                      <sl-tooltip placement="bottom" content="Event Instance" hoist>
                        <div style="display: flex; align-items: center;">
                          <katapult-icon icon="event_note" size="20"></katapult-icon>
                          <span style="margin: 0 var(--sl-spacing-2x-small)">${this.entityLabels.event_instance}</span>
                        </div>
                      </sl-tooltip>
                    </div>
                  </sl-breadcrumb-item>
                `
              : nothing
          }
          ${
            this.conversation.subentity_type == 'ACTION'
              ? html`
                  <sl-breadcrumb-item>
                    <paper-table class="tagContainer" style="border-radius: var(--sl-border-radius-x-large);">
                      <paper-row style="height: unset;">
                        <paper-cell>
                          <user-chip .uid=${this.subentity.uid}></user-chip>
                        </paper-cell>
                        <paper-cell style="flex-direction: column; align-items: flex-start; text-wrap: wrap">
                          <span>${this.subentity.action_name}</span>
                          ${
                            this.subentity.value !== undefined
                              ? html` <span id="actionSubentityLabel">(${this.subentity.value})</span>`
                              : nothing
                          }
                        </paper-cell>
                        <paper-cell style="text-wrap: wrap; font-size: 14px;"
                          >${this.formatDateSeconds(this.subentity?.timestamp?.seconds)}</paper-cell
                        >
                      </paper-row>
                    </paper-table>
                  </sl-breadcrumb-item>
                `
              : nothing
          }
        </sl-breadcrumb>
      </div>
      <div id="commentsContainer" ${ref(this.commentsContainer)} @scroll=${this._updateCommentsContainerAtEnd.bind(this)}>
        ${when(
          !this.commentsLoaded,
          () => html`<sl-spinner style="font-size: 30px; align-self: center;"></sl-spinner>`,
          () =>
            when(
              this.messages.length == 0,
              () => html` <div style="margin-top: 20px; text-align: center; color: rgba(0, 0, 0, 0.6);">(no comments to show)</div>`,
              () => html`
                <sl-resize-observer @sl-resize=${this._updateCommentsContainerAtEnd.bind(this)}>
                  <lit-virtualizer
                    .items=${this.messages}
                    .renderItem=${this._renderMessageTemplate.bind(this)}
                    .keyFunction=${(item) => item.$ref?.id}
                  >
                  </lit-virtualizer>
                </sl-resize-observer>
              `
            )
        )}
      </div>
      <div style="display: flex; flex-direction: column; gap: var(--sl-spacing-2x-small); padding-right: 8px;">
        <div id="messageInputContainer" @drop=${this._handleMessageContainerDropEvent.bind(this)} @dragover=${(e) => e.preventDefault()}>
          <div
            ${ref(this.messageInput)}
            id="messageInput"
            contenteditable="true"
            @keydown=${this.handleKeydownEvent}
            @keyup=${this.maybeShowMentionList}
            @input=${this.handleInputEvent}
          ></div>
          ${!this.userHasDraft ? html`<div id="messageInputLabel">Type message</div>` : nothing}
          ${when(
            messageImageAttachments.length > 0,
            () => html`
              <div id="messageAttachmentsContainer">
                ${map(
                  messageImageAttachments,
                  (attachment) => html`
                    <div class="messageThumbnail">
                      <katapult-icon icon="cancel" @click=${this._removeMessageAttachment.bind(this, attachment)}></katapult-icon>
                      ${until(this._renderAsyncImageTemplate(attachment.previewUrl), this._renderAttachmentLoadingTemplate())}
                    </div>
                  `
                )}
              </div>
            `
          )}
          <div id="messageButtonsContainer">
            ${when(
              !this._isPublicChat,
              () => html`
                <sl-tooltip content="Add Photo">
                  <katapult-button
                    iconOnly
                    noBackground
                    icon="add_photo_alternate"
                    .iconConfig=${{ outline: true }}
                    noBorder
                    .callback=${this._promptUserSelectPhoto.bind(this)}
                  ></katapult-button>
                </sl-tooltip>
              `
            )}
          </div>
          <div id="mentionList" flex ${ref(this.mentionList)} ?hidden=${!this.showMentionList}>
            <paper-table white rounded overflow-hidden outline grow>
              <paper-table-scroll>
                ${map(
                  this.filteredUsernames,
                  (username) => html`
                    <paper-row micro pointer slot="sortable" @click=${(e) => this.insertMention(username)}>
                      <paper-cell>${username}</paper-cell>
                    </paper-row>
                  `
                )}
              </paper-table-scroll>
            </paper-table>
          </div>
        </div>
        <div flex align-center justify-end style="gap: var(--sl-spacing-2x-small);">
          ${
            this._isPublicChat === true
              ? html`
            <sl-tag variant="neutral" style="margin-right: auto" pill>
              <div style="display: flex; align-items: center; justify-content: center; height: -webkit-fill-available;">
                <katapult-icon icon="public" size="16" style="margin-right: var(--sl-spacing-2x-small);"></katapult-icon>
                <span>Public</span>
              <div>
            </sl-tag>`
              : nothing
          }
          <katapult-button outlined @click=${() => this.toggleOpenCloseIssue(hasUnresolvedIssue ? 'RESOLVED' : 'UNRESOLVED')}>
            <div style="position: relative; display: flex; align-items: center; gap: 16px;">
              <sl-tooltip
                hoist
                placement="top"
                content="${hasUnresolvedIssue ? 'There is an open issue on this conversation' : 'No open issues'}"
              >
                <katapult-icon
                  icon=${hasUnresolvedIssue ? 'error' : 'check_circle'}
                  color=${hasUnresolvedIssue ? 'var(--paper-red-500)' : 'var(--paper-green-500)'}
                ></katapult-icon>
              </sl-tooltip>
              <div class="issue-button-divider"></div>
              <div>${hasUnresolvedIssue ? 'Mark as Resolved' : 'Open an Issue'}</div>
            </div>
          </katapult-button>

          <!-- Send Button -->
          <katapult-button reverse icon="send" class="iconLabelButton" outlined .disabled=${!maySendMessage} @click=${this.sendMessage}
            >Comment</katapult-button
          >
        </div>
      </div>
    `;
  }

  constructor() {
    super();

    this.entityLabels = {};
    this.subentity = {};
    this.conversation = {};
    this.conversationRef = null;
    this.conversationListenerUnsub = null;
    this.messages = [];
    this.userIsSubscribed = false;
    this.commentsLoaded = false;
    this.userHasDraft = false;
    this.showMentionList = false;
    this._focused = false;
    this.filteredUsernames = [];
    this.allCompanyUsers = [];
    this.allCompanyUsernames = [];
    /** @type {?string} */
    this.userGroup = null;
    /** @type {?string} */
    this.userId = null;
    this.nodes = {};
    this.mapStyles = {};
    this.conversationRefPath = null;

    this.messageInput = createRef();
    this.commentsContainer = createRef();
    this.mentionList = createRef();
    this.showBackButton = false;
    this._messageAttachments = [];
    this._attachmentLookup = {};
    this._isPublicChat = false;
  }

  connectedCallback() {
    super.connectedCallback();
    // Watch for changes to the user
    this._unsubscribeAuthListener = firebase.auth().onIdTokenChanged((user) => {
      this.userId = user?.uid ?? null;
    });

    // Detect user presence
    this.addEventListener('focus', () => (this._focused = true));
    this.addEventListener('blur', () => (this._focused = false));
    this.setupConversationListeners();
    this._scrollCommentsContainerToEnd();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribeAuthListener?.();
    this.conversationListenerUnsub?.();
  }

  shouldUpdate(changedProperties) {
    if (changedProperties.has('userGroup') && this.userGroup) {
      this.setAllCompanyUsers();
    }
    if (['_focused', 'userId'].some((x) => changedProperties.has(x))) this._focusedChanged();
    if (['conversation', 'userId'].some((x) => changedProperties.has(x))) this.setUserIsSubscribed();
    if (changedProperties.has('conversationRefPath')) {
      this.setConversationRef();
      this._isPublicChat = this.conversationRefPath?.includes('public_feedback');
    }
    if (changedProperties.has('conversationRef')) this.setupConversationListeners();

    return ShouldUpdate(this, changedProperties);
  }

  updated(changedProperties) {
    if (changedProperties.has('messages') && !changedProperties.get('messages')?.length) {
      this._scrollCommentsContainerToEnd();
    }
    this._maintainScrollPosition();
  }

  _maintainScrollPosition() {
    if (this._commentsContainerAtEnd) this._scrollCommentsContainerToEnd();
  }

  _updateCommentsContainerAtEnd() {
    const commentsContainer = this.commentsContainer.value;
    if (!commentsContainer) return false;
    const { scrollTop, scrollHeight, clientHeight } = commentsContainer;
    const scrollHeightChanged = scrollHeight !== this._prevScrollHeight;
    // Assume a scroll height change means the user has not caused the scroll event.
    if (scrollHeightChanged) this._maintainScrollPosition();
    // Otherwise, update whether the user is at the end of the comments container or not.
    else {
      const scrollWindowBottom = scrollTop + clientHeight;
      const distanceToBottom = scrollHeight - scrollWindowBottom;
      this._commentsContainerAtEnd = distanceToBottom < 50;
    }
    this._prevScrollHeight = scrollHeight;
  }

  _scrollCommentsContainerToEnd() {
    const commentsContainer = this.commentsContainer.value;
    if (!commentsContainer) return;
    commentsContainer.scrollTop = commentsContainer.scrollHeight;
    this._commentsContainerAtEnd = true;
  }

  // TODO (06-16-2022): This is not working
  _focusedChanged() {
    // If the chat gains focus and the user has unread comments, mark the conversation as read
    if (this._focused && this.userId) this.markConversationAsRead();
  }

  async toggleOpenCloseIssue(newStatus) {
    // handle the status change
    await this.conversationRef.update({ issue_status: newStatus });

    // Post the event comment
    const username = this.allCompanyUsers.find((x) => x.uid == this.userId).$username;
    const message = `${username} has ${newStatus === 'RESOLVED' ? 'closed' : 'opened'} an issue`;
    const eventType = newStatus === 'RESOLVED' ? 'issue_close' : 'issue_open';
    this.sendEvent(eventType, message);

    // Track opening or closing issues
    logger.info(`${eventType === 'issue_open' ? 'Opened' : 'Closed'} feedback issue`);
  }

  formatDateSeconds(s) {
    return s ? `${new Date(s * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ${FormatDate(s * 1000)}` : '';
  }

  async setAllCompanyUsers() {
    if (!this.userGroup || this.userGroup == '_custom_auth') return;

    let usersById = await FirebaseWorker.ref(`photoheight/company_space/${this.userGroup}/users`)
      .once('value')
      .then((s) => s.val());
    this.allCompanyUsers = ToArray(usersById)
      .filter((user) => !!user.email)
      .map((x) => ({ $username: x.email.split('@')[0], ...x }));
    this.allCompanyUsernames = this.allCompanyUsers.map((x) => x.$username).sort();
  }

  getCommentClassMap(message) {
    const classMap = {};
    if (message.sent_by == this.userId) classMap.commentSent = true;
    else classMap.commentReceived = true;
    if (message.type == 'event') classMap.event = true;
    return classMap;
  }

  openBreadcrumb(conversation, scope) {
    if (!conversation || !scope) return;
    const linkAddress = this.getBreadcrumbLinkAddress(conversation, scope);
    if (linkAddress) window.open(linkAddress, '_blank');
  }

  getBreadcrumbLinkAddress(conversation, scope) {
    switch (scope) {
      case 'node':
        return `../map/#${conversation.job_id}/n${conversation.entity_id}`;
      case 'job':
        return `../map/#${conversation.job_id}`;
    }
  }

  getMessageMentions(message) {
    const possibleMentions = message.match(/@\S+/gm) ?? [];

    let mentions = [];
    for (const mention of possibleMentions) {
      const username = mention.slice(1);
      const user = this.allCompanyUsers.find((x) => x.$username == username);

      if (user) mentions.push(user.$key);
    }

    return mentions;
  }

  getMessageString() {
    const message = this.messageInput?.value?.innerText;
    return message.trim();
  }

  setUserIsSubscribed() {
    if (!this.userId || !this.conversation) return false;
    this.userIsSubscribed = (this.conversation.subscribers ?? []).includes(this.userId);
  }

  _isUrl(string) {
    try {
      new URL(string);
      return true;
    } catch (e) {
      return false;
    }
  }

  handleInputEvent(e) {
    const input = e.currentTarget;
    if (!input) return;
    Array.from(input.childNodes).forEach((child) => {
      switch (child.nodeName) {
        case '#text':
          break;
        case 'SPAN':
          if (child.classList.contains('mention')) break;
        case 'IMG': {
          if (!this._isPublicChat) this._tryToAttachImage(child.src);
          child.remove();
          break;
        }
        default: {
          const childHasTextContent = !!child.textContent;
          // Preserve the text content of the child (required for formatted text which comes in as a div).
          if (childHasTextContent) {
            const textNode = document.createTextNode(child.textContent);
            input.replaceChild(textNode, child);
          } else {
            child.remove();
          }
        }
      }
    });
    this.userHasDraft = !!input.textContent;
  }

  insertMention(username) {
    const input = this.messageInput?.value;
    const span = document.createElement('span');
    span.classList.add('mention');
    span.contentEditable = false;
    replaceActiveWord(input, `@${username}`, span, true);
    this.showMentionList = false;
  }

  maybeShowMentionList(e) {
    const element = e.currentTarget;
    const activeWord = getActiveWord(element);

    if (activeWord.startsWith('@')) {
      this.updateMentionList(activeWord);
    } else this.showMentionList = false;
  }

  handleKeydownEvent(e) {
    // Stop keypresses from escaping the div (and being handled elsewhere)
    e.stopPropagation();

    if (this.showMentionList == true && (e.key == 'Enter' || e.key == 'Tab')) {
      e.preventDefault();
      this.insertMention(this.filteredUsernames[0]);
    }

    // Post a comment if the enter key is pressed
    else if (e.key == 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.sendMessage(e);
    }
  }

  markConversationAsRead() {
    const userHasNotReadConversation = (this.conversation?.unread_by || []).includes(this.userId);
    if (userHasNotReadConversation) this.conversationRef.update({ unread_by: firebase.firestore.FieldValue.arrayRemove(this.userId) });
  }

  async setConversationRef() {
    const { conversationRefPath } = this;
    this.conversationRef = conversationRefPath ? firebase.firestore().doc(conversationRefPath) : null;
  }

  async sendMessage() {
    this._scrollCommentsContainerToEnd();

    // Check if there is a comment to post and abort if it is blank
    const messageText = this.getMessageString();
    if (messageText) {
      const message = {
        text: messageText,
        sent_at: firebase.firestore.Timestamp.now(),
        sent_by: this.userId,
        type: 'message'
      };
      await this.conversationRef.collection('messages').add(message);
      await this.updateConversationAndNotifySubscribers(message);
    }

    // Send any attached images.
    const messageImageAttachments = this._messageAttachments.filter(({ file }) => file?.type.startsWith('image/'));
    if (!this._isPublicChat && messageImageAttachments.length > 0) {
      const username = this.allCompanyUsers.find((x) => x.uid == this.userId).$username;
      for (const attachment of messageImageAttachments) {
        // Compress the file and add it to storage
        const attachmentId = globalThis.crypto.randomUUID();

        // Compress the photo into a thumbnail and full size versions
        const compressedThumbnailImage = await CompressPhoto(attachment.file, { maxDimension: 1024 });
        const compressedFullImage = await CompressPhoto(attachment.file);

        // Add the attachments to storage
        await firebase.storage().ref(`feedback/${this.userGroup}/${attachmentId}_thumbnail`).put(compressedThumbnailImage);
        await firebase.storage().ref(`feedback/${this.userGroup}/${attachmentId}`).put(compressedFullImage);

        // Create the message with the attachment ID and add it to the collection
        const message = {
          text: `${username} sent an image`,
          sent_at: firebase.firestore.Timestamp.now(),
          sent_by: this.userId,
          type: 'message',
          attachment_id: attachmentId
        };
        await this.conversationRef.collection('messages').add(message);
        await this.updateConversationAndNotifySubscribers(message);
      }
    }

    // Track sending messages
    logger.info('Sent feedback message');

    // Reset UI
    if (!this.messageInput.value) return;
    this.messageInput.value.textContent = '';
    this.userHasDraft = false;
    this._messageAttachments = [];
  }

  /**
   * @param {ConversationEventTypes} type - The type of event being sent
   * @param {string} messageText - The text of the event
   */
  sendEvent(type, messageText) {
    // Create the event
    const event = {
      text: messageText,
      sent_at: firebase.firestore.Timestamp.now(),
      sent_by: this.userId,
      type: 'event'
    };

    // Add the event to the messages collection and update the conversation
    this.conversationRef.collection('messages').add(event);
    // Only update the conversation for issue open events
    if (type === 'issue_open' || type === 'issue_close')
      this.updateConversationAndNotifySubscribers(event, { doNotNotify: type === 'issue_close' });
  }

  /**
   * Update the conversation with the last message sent
   * @param {Object} lastMessage - The last message sent
   * @param {Object} [options] - Options for the update
   * @param {boolean} [options.doNotNotify] - If true, do not notify subscribers
   */
  async updateConversationAndNotifySubscribers(lastMessage, options) {
    const { doNotNotify = false } = options ?? {};
    const senderUid = lastMessage.sent_by;
    const timestamp = lastMessage.sent_at;
    const messageText = lastMessage.text;

    // If there are any mentions in this comment, get them
    const mentions = this.getMessageMentions(messageText);

    // Create the subscribers list
    const subscribers = [...new Set([senderUid, ...this.conversation.subscribers, ...mentions])];

    // Ensure that the subentity actor is added to the subscribers list
    if (this.subentity) {
      const subEntityActor = this.conversation.subentity_type === 'ACTION' ? this.subentity?.uid : this.subentity?.set_by;
      if (subEntityActor && !subscribers.includes(subEntityActor)) subscribers.push(subEntityActor);
    }

    // Update the conversation in firestore
    await this.conversationRef.update({
      updated_by: senderUid,
      updated_at: timestamp,
      last_message: messageText,
      subscribers,
      // Mark the conversation as unread and queued for everyone except the sender
      ...(!doNotNotify && {
        unread_by: subscribers.filter((uid) => uid !== senderUid),
        queued_for: subscribers
      }),
      // If the conversation is blank, add this user as the "starter"
      ...(this.conversation.started_by == null && {
        started_by: senderUid,
        started_at: timestamp
      })
    });

    if (this.conversation.entity_type === 'NODE' && !this._isPublicChat && this.userGroup && this.userId) {
      const addFeedbackAttributeFlag = await isFlagEnabled('add_feedback_attribute', globalThis.FirebaseWorker.database(), {
        companyId: this.userGroup,
        userId: this.userId
      });
      if (addFeedbackAttributeFlag) await this.addFeedbackAttribute();
    }
  }

  /* Connect the element to a conversation */
  async setupConversationListeners() {
    this.conversationListenerUnsub?.();

    if (!this.conversationRef) return;

    const disconnectConversationListener = this.conversationRef.onSnapshot(async (s) => {
      this.conversation = s.data();
      this.entityLabels = await getFeedbackEntityLabels(this.conversation);
      // Mark the conversation as read
      // TODO (05/05/2025): Not really sure what to do with the code below
      // if (this.$.feedbackDialog.opened)
      this.markConversationAsRead();

      if (this.conversation?.subentity_type == 'ACTION') {
        const docRef = firebase.firestore().doc(`companies/${this.userGroup}/action_tracking/${this.conversation.subentity_id}`);
        this.subentity = (await docRef.get()).data() ?? {};
      } else if (this.conversation?.subentity_type == 'ATTRIBUTE_EVENT') {
        const docRef = firebase
          .firestore()
          .doc(
            `companies/${this.userGroup}/projects/${this.conversation.project_id}/attribute_events_latest/${this.conversation.subentity_id}`
          );
        this.subentity = (await docRef.get()).data() ?? {};
      }
    });

    const disconnectMessagesListener = this.conversationRef.collection('messages').onSnapshot((s) => {
      this.messages = s.docs.map((x) => ({ $ref: x.ref, ...x.data() })).sort((a, b) => a.sent_at.toMillis() - b.sent_at.toMillis());
      this.commentsLoaded = true;
    });

    this.conversationListenerUnsub = () => {
      disconnectConversationListener();
      disconnectMessagesListener();
      this.conversation = {};
      this.entityLabels = {};
      this.subentity = {};
      this.messages = [];
      this.commentsLoaded = false;
      this.conversationListenerUnsub = null;
    };
  }

  async toggleJoinLeaveConversation() {
    const userIsLeaving = this.userIsSubscribed;

    // If the user was already watching, remove them from the subscribers array; otherwise, add them
    const fieldValueOperation = userIsLeaving
      ? firebase.firestore.FieldValue.arrayRemove(this.userId)
      : firebase.firestore.FieldValue.arrayUnion(this.userId);
    this.conversationRef.update({ subscribers: fieldValueOperation });

    // Post the event comment
    const username = this.allCompanyUsers.find((x) => x.uid == this.userId).$username;
    const eventType = userIsLeaving ? 'conversation_leave' : 'conversation_join';
    const message = `${username} has ${userIsLeaving ? 'left' : 'joined'} the conversation`;
    this.sendEvent(eventType, message);

    // Track joining/leaving conversations
    logger.info(`${userIsLeaving ? 'Left' : 'Joined'} feedback conversation`);

    // TODO (05/06/2025): Close the conversation dialog if "this.userIsSubscribed" it true
  }

  sendToInbox() {
    if (this.conversationRef == null) return;
    moveConversationToUserInbox(this.conversationRef, this.userId);
  }

  removeFromInbox() {
    if (this.conversationRef == null) return;
    removeConversationFromUserInbox(this.conversationRef, this.userId);

    // TODO (05/06/2025): Close the conversation dialog when the new inbox element is implemented
  }

  updateMentionList(activeWord) {
    const searchText = activeWord.slice(1);
    this.filteredUsernames = this.allCompanyUsernames.filter((x) => x.includes(searchText));

    // Update mention list styles
    const messageInput = this.messageInput.value;
    if (!messageInput) return;
    const cursorPosition = getSelectionCoords(messageInput, true);
    const inputPosition = messageInput.getBoundingClientRect();

    const mentionList = this.mentionList.value;
    if (mentionList?.style) {
      mentionList.style.left = `${cursorPosition?.x - inputPosition.left + 16}px`;
      mentionList.style.bottom = `${inputPosition.bottom - cursorPosition?.y + 16}px`;
    }

    // Show the list (if it isn't shown already)
    this.showMentionList = true;
  }

  async addFeedbackAttribute() {
    const update = {};
    const users = this.conversation.queued_for;
    let usersFeedbackAttribute = '';
    for (const user of users) {
      const email = this.allCompanyUsers.find((x) => x.uid == user)?.email || user;
      usersFeedbackAttribute += `${email}, `;
    }
    const shouldAddFeedbackAttribute = this.conversation?.issue_status == 'UNRESOLVED';
    const jobId = this.conversation.job_id;
    const nodeId = this.conversation.entity_id;
    update[`attributes/feedback_users/`] = shouldAddFeedbackAttribute ? { auto_added: usersFeedbackAttribute } : null;
    await FirebaseWorker.ref(`photoheight/jobs/${jobId}/nodes/${nodeId}`).update(update);
    this.updateStyle(nodeId, jobId);
  }

  async updateStyle(nodeId, jobId) {
    const jobRef = FirebaseWorker.database().ref(`photoheight/jobs/${jobId}`);
    const node = this.nodes[nodeId] ?? {};
    if (!node) return;
    const geoUpdate = {};
    GeofireTools.updateStyle('nodes', nodeId, node, geoUpdate, this.mapStyles);
    await jobRef.update(geoUpdate);
  }

  _promptUserSelectPhoto() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.click();
    input.onchange = async (e) => {
      const files = Array.from(input.files ?? []);
      files.forEach((file) => this._addMessageAttachment(file));
    };
  }

  /**
   * Adds a supported attachment to the message.
   * @param {File} file
   * @returns {void}
   */
  _addMessageAttachment(file) {
    if (!file) return;
    const { type } = file;
    if (!type.startsWith('image/')) return; // TODO: 5/14/2025 Handle other file types someday.
    const attachment = { file };
    if (type.startsWith('image/')) attachment.previewUrl = this._readFileAsDataURL(file);
    this._messageAttachments = [...this._messageAttachments, attachment];
  }

  /**
   * Removes a message attachment.
   * @param {Object} attachment
   * @returns {void}
   */
  _removeMessageAttachment(attachment) {
    this._messageAttachments = this._messageAttachments.filter((x) => x !== attachment);
  }

  /**
   * Reads a file as a Data URL.
   * @param {File} file
   * @returns {Promise<string>} - The Data URL of the file.
   */
  async _readFileAsDataURL(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const { result } = reader;
        if (typeof result !== 'string') return reject(new Error('Failed to read file'));
        resolve(result);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }

  _handleMessageContainerDropEvent(e) {
    e.preventDefault();
    if (this._isPublicChat) return;
    const files = Array.from(e.dataTransfer.files ?? []);
    files.forEach((file) => this._addMessageAttachment(file));
  }

  async _tryToAttachImage(src) {
    const isUrl = this._isUrl(src);
    if (isUrl) {
      const blob = await fetch(src).then((res) => res.blob());
      if (!blob) return;
      const [type, subtype] = blob.type.split('/');
      const file = new File([blob], `${type}.${subtype}`, { type: blob.type });
      this._addMessageAttachment(file);
    } else {
      const file = this._createFileFromDataUrl(src);
      if (file != null) this._addMessageAttachment(file);
    }
  }

  _createFileFromDataUrl(dataUrl) {
    const regex = /^data:(?<mime>[\w\/]+);base64,(?<data>.+)$/;
    const match = dataUrl.match(regex);
    if (match) {
      const { mime, data } = match.groups;
      const byteString = atob(data);
      const buffer = new ArrayBuffer(byteString.length);
      const uint8Array = new Uint8Array(buffer);
      for (let i = 0; i < byteString.length; i++) {
        uint8Array[i] = byteString.charCodeAt(i);
      }
      const [type, subtype] = mime.split('/');
      const file = new File([buffer], `${type}.${subtype}`, { type: mime });
      return file;
    }
  }
}
customElements.define('feedback-chat', FeedbackChat);
